import { useEffect, useRef, useState } from "react";
import { useChat } from "@/context/ChatContext";
import { useAuth } from "@/context/AuthContext";
import { MessageCircle, Send, ArrowLeft, X, Store, User, Shield } from "lucide-react";

const roleIcon = (role) => (role === "seller" ? Store : role === "admin" ? Shield : User);

const timeShort = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d) ? "" : d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

export const ChatWidget = () => {
  const { user } = useAuth();
  const chat = useChat();
  const [draft, setDraft] = useState("");
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [chat?.messages, chat?.activeId, chat?.typingPeer]);

  if (!user || !chat) return null;
  const { conversations, messages, activeId, open, setOpen, unread, typingPeer, openConversation, sendMessage, sendTyping, closeThread } = chat;
  const activeConv = conversations.find((c) => c.conversation_id === activeId);

  const submit = (e) => {
    e.preventDefault();
    if (!draft.trim()) return;
    sendMessage(draft);
    setDraft("");
  };

  return (
    <>
      {!open && (
        <button
          data-testid="chat-launcher"
          onClick={() => setOpen(true)}
          className="fixed z-40 bottom-20 right-4 sm:bottom-6 w-14 h-14 rounded-full bg-primary text-white shadow-xl flex items-center justify-center hover:scale-105 active:scale-95 transition-transform"
          aria-label="Open messages"
        >
          <MessageCircle className="w-6 h-6" />
          {unread > 0 && (
            <span data-testid="chat-unread-badge" className="absolute -top-1 -right-1 min-w-5 h-5 px-1 rounded-full bg-destructive text-white text-[11px] font-bold flex items-center justify-center border-2 border-background">
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </button>
      )}

      {open && (
        <div data-testid="chat-panel" className="fixed z-50 bottom-20 right-4 sm:bottom-6 left-4 sm:left-auto sm:w-96 h-[70vh] sm:h-[540px] max-h-[540px] rounded-2xl border border-border/60 bg-card shadow-2xl flex flex-col overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-border/60 bg-primary text-white">
            {activeId ? (
              <button data-testid="chat-back-btn" onClick={closeThread} className="p-1 -ml-1 rounded hover:bg-white/15"><ArrowLeft className="w-5 h-5" /></button>
            ) : (
              <MessageCircle className="w-5 h-5" />
            )}
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm truncate">{activeConv?.peer?.name || "Messages"}</p>
              {activeConv?.product_title && <p className="text-[11px] text-white/80 truncate">Re: {activeConv.product_title}</p>}
            </div>
            <button data-testid="chat-close-btn" onClick={() => setOpen(false)} className="p-1 rounded hover:bg-white/15"><X className="w-5 h-5" /></button>
          </div>

          {!activeId ? (
            <div className="flex-1 overflow-y-auto">
              {conversations.length === 0 && (
                <div className="h-full flex flex-col items-center justify-center text-center px-6 text-muted-foreground">
                  <MessageCircle className="w-10 h-10 mb-3 opacity-40" />
                  <p className="text-sm font-medium">No conversations yet</p>
                  <p className="text-xs mt-1">Message a seller from any product page to get started.</p>
                </div>
              )}
              {conversations.map((c) => {
                const Icon = roleIcon(c.peer?.role);
                return (
                  <button
                    key={c.conversation_id}
                    data-testid={`chat-conv-${c.conversation_id}`}
                    onClick={() => openConversation(c.conversation_id)}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/50 transition-colors text-left border-b border-border/40"
                  >
                    <span className="w-10 h-10 rounded-full bg-muted flex items-center justify-center shrink-0 overflow-hidden">
                      {c.peer?.picture ? <img src={c.peer.picture} alt="" className="w-full h-full object-cover" /> : <Icon className="w-5 h-5 text-muted-foreground" />}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="flex items-center justify-between gap-2">
                        <span className="font-medium text-sm truncate">{c.peer?.name || "Unknown"}</span>
                        <span className="text-[10px] text-muted-foreground shrink-0">{timeShort(c.updated_at)}</span>
                      </span>
                      <span className="text-xs text-muted-foreground truncate block">{c.last_message || "No messages yet"}</span>
                    </span>
                    {c.unread > 0 && <span className="min-w-5 h-5 px-1 rounded-full bg-primary text-white text-[11px] font-bold flex items-center justify-center">{c.unread}</span>}
                  </button>
                );
              })}
            </div>
          ) : (
            <>
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2 bg-muted/20">
                {messages.map((m) => {
                  const mine = m.sender_id === user.user_id;
                  return (
                    <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[78%] rounded-2xl px-3 py-2 text-sm ${mine ? "bg-primary text-white rounded-br-sm" : "bg-card border border-border/60 rounded-bl-sm"}`}>
                        <p className="whitespace-pre-wrap break-words">{m.text}</p>
                        <p className={`text-[10px] mt-0.5 ${mine ? "text-white/70" : "text-muted-foreground"}`}>{timeShort(m.created_at)}</p>
                      </div>
                    </div>
                  );
                })}
                {typingPeer && <p className="text-xs text-muted-foreground italic px-2">typing…</p>}
              </div>
              <form onSubmit={submit} className="flex items-center gap-2 p-3 border-t border-border/60">
                <input
                  data-testid="chat-message-input"
                  value={draft}
                  onChange={(e) => { setDraft(e.target.value); sendTyping(); }}
                  placeholder="Type a message…"
                  className="flex-1 rounded-full border border-border/60 bg-background px-4 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/40"
                />
                <button data-testid="chat-send-btn" type="submit" className="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center shrink-0 disabled:opacity-50" disabled={!draft.trim()}>
                  <Send className="w-4 h-4" />
                </button>
              </form>
            </>
          )}
        </div>
      )}
    </>
  );
};
